<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form @submit="editMenu" :validation-schema="MenuSchema">
        <div class="row">
          <!-- Titre -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">
                Titre <span class="text-danger">*</span>
              </label>
              <Field
                name="titre"
                type="text"
                class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.titre"
              />
              <ErrorMessage name="titre" class="text-danger"/>
            </div>
          </div>

          <!-- Position -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">
                Position <span class="text-danger">*</span>
              </label>
              <Field
                name="position"
                type="text"
                class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.position"
              />
              <ErrorMessage name="position" class="text-danger"/>
            </div>
          </div>

          <!-- Lien -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">
                Lien <span class="text-danger">*</span>
              </label>
              <Field
                name="lien"
                type="text"
                class="form-control shadow-none fs-md-15 text-black"
                v-model="formData.lien"
              />
              <ErrorMessage name="lien" class="text-danger"/>
            </div>
          </div>

          <!-- Menu parent -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">
                Menu Parent
              </label>
              <Field name="menu_parent_id" v-slot="{ field }">
                <Multiselect
                  :options="MenusOptions"
                  v-model="selectedParentId"
                  track-by="value"
                  label="label"
                  placeholder="Sélectionner un menu parent (facultatif)"
                  :can-clear="true"
                  @change="field.onChange($event)"
                />
              </Field>
              <ErrorMessage name="menu_parent_id" class="text-danger" />
            </div>
          </div>

          <!-- Categorie Articles -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">
                Catégories
              </label>
              <Field name="categorie_ids" v-slot="{ field }">
                <Multiselect
                  :options="categorieArticlesOptions"
                  v-model="selectedCategorieIds"
                  track-by="value"
                  label="label"
                  mode="tags"
                  placeholder="Sélectionner des catégories"
                  @select="field.onChange(selectedCategorieIds)"
                  @remove="field.onChange(selectedCategorieIds)"
                />
              </Field>
              <ErrorMessage name="categorie_ids" class="text-danger" />
            </div>
          </div>

          <!-- Image principale -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">Image principale</label>
              <input
                ref="imageInput"
                type="file"
                name="image"
                accept="image/jpeg, image/png"
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="image" class="text-danger"/>
            </div>
          </div>

          <!-- Icone -->
          <div class="col-md-6">
            <div class="form-group mb-15">
              <label class="d-block text-black fw-semibold mb-10">Icone</label>
              <input
                ref="iconeInput"
                type="file"
                name="icone"
                accept="image/jpeg, image/png"
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="icone" class="text-danger"/>
            </div>
          </div>

          <!-- Boutons -->
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button type="submit"
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 fs-lg-16 bg-success">
                Modifier
              </button>
              <router-link to="/liste-menus" class="btn btn-danger transition border-0">
                Annuler
              </router-link>
            </div>
          </div>

        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, onMounted } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import { useRoute, useRouter } from "vue-router";
import Multiselect from '@vueform/multiselect'
import ApiService from "../../services/ApiService";
import { error, success } from "../../utils/utils";

export default defineComponent({
  name: "EditMenu",
  components: { Form, Field, ErrorMessage, Multiselect },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const menuId = Number(route.params.id);

    const MenuSchema = Yup.object().shape({
      titre: Yup.string().required("Le titre est obligatoire"),
      position: Yup.mixed().required("La position est obligatoire"),
      lien: Yup.mixed().required("Le lien est obligatoire"),
      image: Yup.mixed().notRequired(),
      icone: Yup.mixed().notRequired()
    });

    const formData = reactive({
      titre: "",
      position: "",
      lien: ""
    });

    const selectedParentId = ref<number | null>(null);
    const selectedCategorieIds = ref<number[]>([]);
    const MenusOptions = ref([]);
    const categorieArticlesOptions = ref<any[]>([]);
    const imageInput = ref<HTMLInputElement | null>(null);
    const iconeInput = ref<HTMLInputElement | null>(null);

    const fetchMenus = async () => {
      try {
        const response = await ApiService.get('/menus');
        const menusData = response.data.data.data;
        MenusOptions.value = menusData.map((menu) => ({
          value: menu.id,
          label: `${menu.titre}`
        }));

      } catch (error) {
        //
      }
    };

    const fetchCategorieArticles = async () => {
      try {
        const response = await ApiService.get('/categorieArticles');
        const categorieData = response.data.data.data;
        const categoriesData = categorieData.filter(
          (categorie: any) => categorie.isService === "Non"
        );
        categorieArticlesOptions.value = categoriesData.map((cat) => ({
          value: cat.id,
          label: `${cat.nom}`,
        }));
      } catch (error) {
        console.error(error);
      }
    };

    const getMenu = async (id: number) => {
      try {
        const res = await ApiService.get(`/menu/${id}`);
        const menu = res.data.data;
        console.log('uuuuuu',menu)
        formData.titre = menu.titre || "";
        formData.position = menu.position || "";
        formData.lien = menu.lien || "";

        selectedParentId.value = menu.menu?.id || null;
        selectedCategorieIds.value = menu.categorieArticles?.map((c: any) => c.id) || [];

      } catch (err: any) {
        console.error(err);
        error(err.response?.data?.message || "Erreur lors du chargement du menu");
      }
    };

    const editMenu = async () => {
      try {
        const fd = new FormData();
        fd.append("titre", formData.titre);
        fd.append("position", formData.position);
        fd.append("lien", formData.lien);

        if (selectedParentId.value) fd.append("menu_parent_id", selectedParentId.value.toString());
        selectedCategorieIds.value.forEach(id => fd.append("categorie_ids[]", id.toString()));

        if (imageInput.value?.files?.[0]) fd.append("image", imageInput.value.files[0]);
        if (iconeInput.value?.files?.[0]) fd.append("icone", iconeInput.value.files[0]);

        const res = await ApiService.put(`/menus/${menuId}`, fd, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });


        if (res.data.code === 200) {
          success(res.data.message);
          router.push("/liste-menus");
        }
      } catch (err: any) {
        console.error(err);
        error(err.response?.data?.message || "Erreur lors de la modification");
      }
    };

    onMounted(() => {
      if (menuId && !isNaN(menuId)) getMenu(menuId);
      fetchCategorieArticles(),
       fetchMenus()
    },
      )
    

    return {
      MenuSchema,
      formData,
      editMenu,
      selectedParentId,
      selectedCategorieIds,
      MenusOptions,
      categorieArticlesOptions,
      imageInput,
      iconeInput
    };
  }
});
</script>
